<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import { PRODUCT_ICON_NAME } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import Avatar from '@/components/common/atoms/avatar.vue';
import ActionBalloon from '@/components/common/molecules/balloon.vue';
import { ShareSurveyMode } from '@/types';
import { useOutsideClick } from '@/utils/composables/useOutsideClick';
import { useTooltipVisibility } from '@/utils/composables/useTooltipVisibility';

interface Props {
  title: string;
  userName: string;
  userIconUrl?: string;
  useAnewsIcon?: boolean;
  isOwner: boolean;
  mode: ShareSurveyMode;
  updatedAt: string;
}

const props = defineProps<Props>();

const menuRef = ref<Element>();
const isClickOutside = useOutsideClick([menuRef]);
const {
  elementRef: titleRef,
  isTooltipVisible,
  checkTooltipVisibility,
} = useTooltipVisibility();
watch(isClickOutside, isOutside => {
  if (isOutside) closeMenu();
  isClickOutside.value = false;
});

watch(
  () => props.title,
  () => {
    // タイトル変更時はtooltip表示判定を再実行
    nextTick(() => {
      checkTooltipVisibility();
    });
  },
);

const isOpenMenu = ref(false);
const clickMenu = () => {
  isOpenMenu.value = !isOpenMenu.value;
};
const closeMenu = () => {
  isOpenMenu.value = false;
};

type Emits = {
  delete: [];
  share: [];
  editTitle: [];
};
const emit = defineEmits<Emits>();

const deleteThread = () => {
  closeMenu();
  emit('delete');
};

const editTitle = () => {
  closeMenu();
  emit('editTitle');
};

const shareThread = () => {
  emit('share');
};
</script>

<template>
  <div class="share-survey-header">
    <div class="header-content">
      <div class="header-content-left">
        <VTooltip
          :disabled="!isTooltipVisible"
          placement="bottom"
          :prevent-overflow="true"
        >
          <div class="c-text-regular--m header-title">
            <span ref="titleRef">
              {{ title }}
            </span>
          </div>
          <template #popper>{{ title }}</template>
        </VTooltip>
        <div class="header-info c-text-regular--s">
          <div class="user-info">
            <div v-if="useAnewsIcon">
              <DgrIcon :name="PRODUCT_ICON_NAME" size="small" />
            </div>
            <Avatar v-else :custom-size="16" :image-url="userIconUrl" />
            <div>{{ userName }}</div>
          </div>
          <div class="updated-at">更新日：{{ updatedAt }}</div>
        </div>
      </div>
      <div class="header-content-right">
        <div class="header-menu" v-if="isOwner">
          <button
            class="header-menu-button c-btn--auto c-btn"
            @click.stop.prevent="clickMenu"
          >
            <DgrIcon name="ellipsis-h" size="small" />
          </button>
          <div ref="menuRef">
            <ActionBalloon
              position="absolute"
              :is-show="isOpenMenu"
              balloon-position="right"
            >
              <div class="header-menu-item c-title-bold--m" @click="editTitle">
                <DgrIcon name="pencil" size="small" />
                タイトルを編集
              </div>
              <div class="separator"></div>
              <div
                class="header-menu-item c-title-bold--m"
                @click="deleteThread"
              >
                <DgrIcon name="trash" size="small" />
                シェアスレッドを削除
              </div>
            </ActionBalloon>
          </div>
        </div>
        <button
          v-if="mode === 'create'"
          class="c-btn c-btn--auto c-btn--AnewsPrimary share-button"
          @click="shareThread"
        >
          共有
        </button>
        <button
          v-else
          class="c-btn c-btn--auto c-btnOutline share-button"
          @click="shareThread"
        >
          <span v-if="mode === 'edit'"> 共有設定編集 </span>
          <span v-else-if="mode === 'view'"> 共有設定 </span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.share-survey-header {
  padding: 16px 24px;
  display: flex;
  justify-content: center;
  background-color: $color-white;
  .header-content {
    max-width: var(--survey-session-width);
    width: 100%;
    display: flex;
    gap: 10px;
    justify-content: space-between;
  }
  .header-content-left {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    .header-title {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: 532px;
    }
    .header-info {
      padding: 4px 0;
      color: $color-gray800;
      display: flex;
      gap: 8px;
      .user-info {
        display: flex;
        gap: 4px;
      }
      .updated-at {
        white-space: nowrap;
      }
    }
  }
  .header-content-right {
    display: flex;
    align-items: center;
    gap: 10px;
    .share-button {
      white-space: nowrap;
    }
    .header-menu {
      position: relative;
      .separator {
        margin: 12px 0;
        border-top: 1px solid $color-gray400;
      }
    }
    .header-menu-button {
      padding: 11px;
      border-radius: 4px;
      border: none;
      &:hover {
        background: $color-gray200;
        cursor: pointer;
      }
    }
    .header-menu-item {
      width: 160px;
      padding: 5px 16px;
      display: flex;
      gap: 4px;
      justify-content: start;
      align-items: center;
      &:hover {
        background: $color-gray200;
        cursor: pointer;
      }
    }
  }
}
</style>
